import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "../styles/AIPhilosophy.css";

const AIPhilosophy: React.FC = () => {
  const applications = [
    {
      title: "Writing Revision",
      description:
        "I love to write, but it is because I love to write that I scarcely find myself wanting to generate something and just claim it. However in my endeavors to publish massive 700-page novels I've used tools such as Prowritingaid & Claude to proof read what I wrote, and in many cases suggest copy or line edits.",
      icon: "✍️",
    },
    {
      title: "Rapid Code Prototyping",
      description:
        "Claude Code has been a godsend. And I say this as a developer and programmer. Countless ideas that I could not get around to spending months of my life to bring to a screen I've been able to test out in a matter of days. With that said, the amounts of times it has frustrated me and I've had to just fix things myself has kept me grounded in the reality that I can't hang my keyboard just yet, regrettably.",
      icon: "💻",
    },
    {
      title: "Unborn Art",
      description:
        "I call it that, because without AI-Generated Art - that I tend to then throw into photoshop and modify, there simply never would have been a replacement for the art generated - I'm never commissioning artists for a throwaway D&D scene for instance nor am I spending hours of my life painting it. And I dedicate this entire point to things like midjourney which I do use often, but I also wish to hold myself to this in any commercial endeavor that shows promise: Human-made art by an artist (even should it be AI-Assissted) will always be my preference over art I can generate myself cheaply.",
      icon: "🖼️",
    },
    {
      title: "Music",
      description:
        "I must have generated over 10k songs, of which you can find a bunch of albums on this website. Similar to art, it is simply something I never thought I would hear. Songs that I wrote being performed to a full orchestra or to a symphonic metal band. That said, I do not force it upon anyone, nor do I sell it.",
      icon: "🎸",
    },
  ];

  return (
    <div className="ai-philosophy">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Link to="/" className="back-button">
          <span className="back-arrow">←</span>
          <span>Back</span>
        </Link>
      </motion.div>

      <div className="philosophy-container">
        {/* Header */}
        <motion.header
          className="philosophy-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>On AI & Creativity</h1>
          <p className="header-subtitle">
            It is a tool like any other, though one that is readily available
            and thus is more often misused than not.
          </p>
        </motion.header>

        {/* Main Statement */}
        <motion.section
          className="philosophy-statement"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2>Symbiotic Relationship</h2>
          <p>
            It is my view that the public rage and outcry within creative
            circles surrounding the use of AI in anything even tangentially
            creative is a symptom of fear of change paired with far too many
            examples of misuse.
          </p>
          <p>
            The sooner both sides of this argument see that AI tools will NOT
            make humans redundant, but merely change how they work, the sooner
            we would begin to see real progress. In fact, I reckon that
            creatives that are fearful of being 'replaced' by AI are themselves
            in the best position to use it, but find it easier to lament it as
            some form of brainrot.
          </p>
          <h2>The Only Constant</h2>
          <p>
            A Cliché it might be, but an accurate one. To say that change is the
            only constant. And I do genuinely appreciate that the advent of AI
            (or what we have taken to calling AI) is a massive one. However, it
            is no more massive than the advent of the engine, or the car, or the
            phone, or the computer. Each of these can be seen to have 'put
            people out of jobs', but did they really?
          </p>
          <p>
            PCs & Excel did not remove the need for accountants, it simply
            changed their role from needing to calculate by hand, or parse
            documents, and check and doublecheck for human errors, into a more
            autonomous and certain function.
          </p>
          <p>
            Not to put too fine a point on it, but in the end the human element
            will always be vital. No matter how advanced our tools get. I do
            agree that the easier the tools become to pick up, the more we shall
            get of those with no real substance wielding them, but I am certain
            that those with quality will outshine whatever perceived
            tool-deficient there might be.
          </p>
        </motion.section>

        {/* Applications */}
        <motion.section
          className="philosophy-applications"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2>How I Use AI - Acceleration & Iteration</h2>
          <div className="applications-grid">
            {applications.map((app, index) => (
              <motion.div
                key={index}
                className="application-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
              >
                <span className="app-icon">{app.icon}</span>
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Ethics */}
        <motion.section
          className="philosophy-ethics"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2>Ethical Considerations</h2>
          <p>
            My stance so far does not contradict my understanding that it is
            simply not for everyone. That is fair, and hence I do believe one
            should endeavor to keep these considerations in mind.
          </p>
          <ul className="ethics-list">
            <li>
              <strong>Transparency:</strong> Being clear about when and how AI
              is used in creative work, and ideally a way to switch it off when
              possible, like in a game.
            </li>
            <li>
              <strong>Restraint:</strong> Just because something can be done,
              doesn't meant it should be done. AI Empowers, it is up to us to
              use it wisely.
            </li>
            <li>
              <strong>Human First:</strong> I do not say this to placate, I say
              this because I am convinced of it. People make up a team -
              whatever function they carry, AI-Assistants do not.
            </li>
          </ul>
        </motion.section>

        {/* Closing */}
        <motion.section
          className="philosophy-closing"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <p>
            Time will tell if mine was a fair stance to take on this matter, but
            as of now I am positive that the best way forward is one where we
            recognize this tool's strength, and treat it as one. Evolving
            ourselves where need be to wield a new, and frightfully powerful at
            times tool.
          </p>
        </motion.section>
      </div>
    </div>
  );
};

export default AIPhilosophy;
